/* eslint-disable prettier/prettier */
import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { travel, axiosConfig, rappiServicesTravelSearchHotelContent } from 'api';
import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { Dispatch } from 'redux';
import { RootState } from 'store';

let cancel;
const CancelToken = axios.CancelToken;

import {
  hotelsSearchRequest,
  hotelsSearchSuccessful,
  hotelsSearchError,
  hotelsResultsError,
  hotelsResultsSuccessful,
  hotelsResultsRequest,
  hotelsClear,
} from 'action-creators/hotels';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { HotelDetailsCompleteResponse, RoomRatePrice } from 'models/hotelDetailsComplete';
import { HotelFilter, HotelFilterResponse } from 'models/hotelFilter';
import { TravelHotelContentLocationRequest, TravelHotelContentLocationResponse } from 'models/hotelLocation';
import { HotelSolicitationRequest, HotelBook, HotelDetailsForHotelBook, Traveler } from 'models/hotelSolicitation';
import {
  HotelsSearchRequest,
  HotelsSearchResponse,
  HotelsResultRequest,
  HotelsResultResponse,
  HotelsResultRequestOrderCriteriaEnum,
} from 'models/hotelsSearch';
import { HotelValidateResponse } from 'models/hotelValidate';
import { Response } from 'models/response';
import { amplitudelogEvent, TravelErrorEvent } from 'services/amplitudeService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';

import { daysCount } from './../utils/generic';

type SearchHotelArguments = {
  destinationPoint: TravelHotelContentLocationRequest;
  checkInDate: Date;
  checkOutDate: Date;
  traveler?: TravelArrangerUsersAllowed;
  newSessionToken?: string;
  branchCompany?: GetAllBillingCompanyResponse;
};

export const searchHotels = ({
  destinationPoint,
  checkInDate,
  checkOutDate,
  traveler,
  newSessionToken,
  branchCompany,
}: SearchHotelArguments) => async (dispatch: Dispatch, getState: () => RootState) =>
  new Promise(async (resolve, reject) => {
    dispatch(hotelsSearchRequest({ destinationPoint, checkInDate, checkOutDate, traveler, branchCompany }));

    const { accountResult } = getState().user;
    if (!accountResult) return;

    const { sessionToken, countryCurrency } = accountResult;

    const location: { data } = await rappiServicesTravelSearchHotelContent.get<TravelHotelContentLocationResponse>(
      '',
      {
        params: {
          id: destinationPoint.id,
          isHotel: destinationPoint.isHotel,
          locale: 'es',
        },
        ...axiosConfig,
      },
    );

    try {
      const {
        data: { succeeded, data, errors },
      } = await travel.post<HotelsSearchRequest, AxiosResponse<Response<HotelsSearchResponse>>>('Hotel/HotelSearchV3', {
        locationId: location.data.Id,
        destinationType: location.data.Type,
        checkIn: `${moment(checkInDate).format('YYYY-MM-DD')}T00:00:01Z`,
        checkOut: `${moment(checkOutDate).format('YYYY-MM-DD')}T00:00:02Z`,
        currencyCode: countryCurrency,
        sessionToken: newSessionToken ? newSessionToken : sessionToken,
        rooms: [{ adults: 1 }],
        includeHotelInfo: true,
        includeRooms: false,
        maxResults: 0,
        billingCompanyId: branchCompany?.id,
      });

      if (!succeeded) {
        throw errors;
      }

      dispatch(hotelsSearchSuccessful(data));
      resolve(data);
    } catch (ex) {
      const event: TravelErrorEvent = {
        SOURCE: 'TRAVEL_HOME',
        RESULTS: 0,
        TRAVEL_PRODUCT_TYPE: 'HOTELS',
        TRAVEL_FROM: '',
        TRAVEL_TO: location?.data?.Id ?? '',
        TRAVEL_DESTINATION_TYPE: '',
        TRAVEL_TRIP_TYPE: '',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkInDate).toString(),
        TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
        TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
        SEARCHID: '',
        ERROR_MESSAGE: ex.response?.data?.Errors?.join(','),
      };
      amplitudelogEvent(amplitudeEvents.TRAVEL_SEARCH_ERROR, event, accountResult);

      if (ex.response?.data?.Errors?.some((error: string) => error.includes('NoResults'))) {
        resolve({
          resultsCount: 0,
        });
        return;
      }

      Sentry.addBreadcrumb({
        category: 'hotel',
        message: 'Could not search hotels',
        data: { ex, response: ex.response },
      });
      Sentry.captureMessage('[Hotels] Could not search hotels');
      dispatch(hotelsSearchError(ex));
      reject(ex);
    }
  });

export const hotelResults = (newFilters?: HotelFilter) => async (dispatch: Dispatch, getState: () => RootState) => {
  try {
    const { filters, isEnd, search, isLoadingResults, traveler } = getState().hotels;

    const { accountResult } = getState().user;
    if (!accountResult) return;
    const { sessionToken, id: loggedUserId } = accountResult;

    const maxResultsPerPage = Number(ENV.SEARCH_MAX_RESULT_PER_PAGE);
    const hasFilterChanged = newFilters && !_.isEqual(filters, newFilters);

    if (!search) return;
    const { results, searchId, resultsCount, distanceRestriction } = search;

    if ((isEnd && !newFilters) || (!newFilters && results.length >= resultsCount)) {
      return;
    }

    if (isLoadingResults && (newFilters === undefined || newFilters === null)) return;

    if (isLoadingResults && cancel) cancel();

    dispatch(hotelsResultsRequest(newFilters));

    const orderDesc = newFilters
      ? newFilters?.orderCriteria === HotelsResultRequestOrderCriteriaEnum.CATEGORY
        ? true
        : false
      : filters
      ? filters?.orderCriteria === HotelsResultRequestOrderCriteriaEnum.CATEGORY
        ? true
        : false
      : false;
    const {
      data: { data },
    } = await travel.post<HotelsResultRequest, AxiosResponse<Response<HotelsResultResponse[]>>>(
      'Hotel/HotelResults',
      {
        userId: traveler ? traveler.id || traveler.userId : loggedUserId,
        travelArrangerUserId: traveler && traveler.id !== loggedUserId ? loggedUserId : undefined,
        searchId,
        resultCountLowerBound: hasFilterChanged ? 0 : results.length,
        resultCountUpperBound: hasFilterChanged ? maxResultsPerPage : results.length + maxResultsPerPage,
        distanceRestriction,
        sessionToken,
        orderCriteria: newFilters?.orderCriteria || HotelsResultRequestOrderCriteriaEnum.RECOMENDATION,
        orderDesc: orderDesc,
        includeRooms: false,
        ...(newFilters || filters),
      } as HotelsResultRequest,
      {
        cancelToken: new CancelToken((c) => {
          cancel = c;
        }),
      },
    );

    dispatch(hotelsResultsSuccessful(data));
  } catch (ex) {
    Sentry.addBreadcrumb({
      category: 'hotel',
      message: 'Could not get hotel results',
      data: { ex, response: ex.response },
    });
    Sentry.captureMessage('[Hotels] Could not get hotel results');
    dispatch(hotelsResultsError(ex));
  }
};

export const confirmHotelSolicitation = (
  hotel: HotelDetailsCompleteResponse,
  justifyForBreakingRules: string,
  reason: string,
  roomRateId: string,
  roomRate: { refundableType: string },
  roomRatePrice: RoomRatePrice,
  hotelValidate: HotelValidateResponse,
  costCenterId?: string,
  breakRulesMessageId?: string,
) => (dispatch: Dispatch, getState: () => RootState) =>
  new Promise<void>(async (resolve, reject) => {
    const { accountResult } = getState().user;
    const { search, traveler, destinationPoint, branchCompany } = getState().hotels;
    try {
      if (!accountResult || !search) return;

      let hotelTraveler: Traveler = {
        gender: accountResult.gender,
        firstName: accountResult.name,
        lastName: accountResult.lastName,
        phone: accountResult.phoneNumber,
        email: accountResult.email,
        nationality: accountResult.country,
        dob: new Date(accountResult.birthDate),
        documentNumber: accountResult.documentNumber,
        passportNumber: accountResult.passportNumber,
        roomNumber: 1,
        paxType: 'Adult',
      };

      if (traveler) {
        hotelTraveler = {
          gender: traveler.gender,
          firstName: traveler.name,
          lastName: traveler.lastName,
          phone: traveler.phoneNumber,
          email: traveler.email,
          nationality: traveler.country,
          dob: new Date(traveler.birthDate),
          documentNumber: traveler.documentNumber,
          passportNumber: traveler.passportNumber,
          roomNumber: 1,
          paxType: 'Adult',
        };
      }

      const hotelDetails: HotelDetailsForHotelBook = {
        hotelCode: hotel.hotelCode,
        hotelName: hotel.hotelName,
        address: hotel.address,
        cityCode: hotel.cityCode,
        thumbnail: hotel.thumbnail,
        latitude: hotel.latitude,
        longitude: hotel.longitude,
        category: hotel.category,
        hotelProvider: hotel.hotelProvider,
        cityName: hotel.cityName,
        stateName: hotel.stateName,
        countryName: hotel.countryName,
      };

      const hotelBook: HotelBook = {
        sessionToken: accountResult.sessionToken,
        value: hotel.total,
        currency: hotel.currencyCode,
        checkIn: hotel.checkinDate,
        checkOut: hotel.checkoutDate,
        searchId: hotel.searchId,
        optionId: hotel.optionId,
        distanceRestriction: search.distanceRestriction,
        rateId: roomRateId,
        rateValue: roomRatePrice.total,
        roomRate,
        travelers: [hotelTraveler],
        hotel: hotelDetails,
      };

      await travel.post('Travels/Create', {
        userId: traveler ? traveler.id || traveler.userId : accountResult.id,
        userFullName: traveler
          ? `${traveler.name} ${traveler.lastName}`
          : `${accountResult.name} ${accountResult.lastName}`,
        companyId: accountResult.companyId,
        currency: hotel.currencyCode,
        hotelBook,
        justifyForBreakingRules: justifyForBreakingRules,
        reason,
        travelArrangerUserId: traveler && traveler.id !== accountResult.id ? accountResult.id : undefined,
        costCenterId: costCenterId,
        validationId: hotelValidate.validationId,
        documentId: hotelValidate.documentId,
        billingCompanyId: branchCompany?.id,
        lowestLogicalFareList: search.lowestLogicalFareList,
      } as HotelSolicitationRequest);

      dispatch(hotelsClear());
      resolve();
    } catch (ex) {
      const checkInDate = hotel.checkinDate;
      const checkOutDate = hotel.checkoutDate;
      const event: TravelErrorEvent = {
        SOURCE: 'TRAVEL_HOME',
        RESULTS: 0,
        TRAVEL_PRODUCT_TYPE: 'HOTELS',
        TRAVEL_FROM: '',
        TRAVEL_TO: destinationPoint?.id.toString() ?? '',
        TRAVEL_DESTINATION_TYPE: search?.distanceRestriction ?? '',
        TRAVEL_TRIP_TYPE: '',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkInDate).toString(),
        TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
        TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
        SEARCHID: hotel.searchId ?? '',
        ERROR_MESSAGE: ex.toString(),
      };
      amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult);

      reject(ex);
    }
  });

export const getHotelDetails = async ({
  sessionToken,
  searchId,
  optionId,
  culture,
  distanceRestriction,
  userId,
  travelArrangerUserId,
}: {
  sessionToken: string;
  searchId: string;
  optionId: number;
  culture: string;
  distanceRestriction: string;
  userId: string;
  travelArrangerUserId?: string;
}): Promise<AxiosResponse<Response<HotelDetailsCompleteResponse>>> =>
  travel.get('Hotel/HotelDetailsCompleteV2', {
    params: {
      sessionToken,
      searchId,
      hotelOptionId: optionId,
      language: culture,
      distanceRestriction,
      userId,
      travelArrangerUserId,
    },
    ...axiosConfig,
  });

export const getFilters = () => async (_: Dispatch, getState: () => RootState) =>
  new Promise<HotelFilterResponse>(async (resolve, reject) => {
    try {
      const { accountResult } = getState().user;
      const { search } = getState().hotels;

      const {
        data: { data },
      } = await travel.get<Response<HotelFilterResponse>>('Hotel/HotelFilter', {
        params: {
          sessionToken: accountResult?.sessionToken,
          searchId: search?.searchId,
        },
        ...axiosConfig,
      });

      resolve(data);
    } catch (ex) {
      Sentry.addBreadcrumb({
        category: 'hotel',
        message: 'Could not get hotel filters',
        data: { ex, response: ex.response },
      });
      Sentry.captureMessage('[Hotels] Could not get hotel filters');
      reject(ex);
    }
  });

export const validateHotel = (
  hotel: HotelDetailsCompleteResponse,
  justifyForBreakingRules: string,
  reason: string,
  roomRateId: string,
  roomRate: { refundableType: string },
  roomRatePrice: RoomRatePrice,
  redux: RootState,
  costCenterId?: string,
) =>
  new Promise<any>(async (resolve, reject) => {
    const { accountResult } = redux.user;
    const { search, traveler, destinationPoint, branchCompany } = redux.hotels;

    if (!accountResult || !search) {
      return;
    }

    try {
      let hotelTraveler: Traveler = {
        gender: accountResult.gender,
        firstName: accountResult.name,
        lastName: accountResult.lastName,
        phone: accountResult.phoneNumber,
        email: accountResult.email,
        nationality: accountResult.country,
        dob: new Date(accountResult.birthDate ?? ''),
        documentNumber: accountResult.documentNumber,
        passportNumber: accountResult.passportNumber,
        roomNumber: 1,
        paxType: 'Adult',
      };

      if (traveler) {
        hotelTraveler = {
          gender: traveler.gender,
          firstName: traveler.name,
          lastName: traveler.lastName,
          phone: traveler.phoneNumber,
          email: traveler.email,
          nationality: traveler.country,
          dob: new Date(traveler.birthDate),
          documentNumber: traveler.documentNumber,
          passportNumber: traveler.passportNumber,
          roomNumber: 1,
          paxType: 'Adult',
        };
      }

      const hotelDetails: HotelDetailsForHotelBook = {
        hotelCode: hotel.hotelCode,
        hotelName: hotel.hotelName,
        address: hotel.address,
        cityCode: hotel.cityCode,
        thumbnail: hotel.thumbnail,
        latitude: hotel.latitude,
        longitude: hotel.longitude,
        category: hotel.category,
        hotelProvider: hotel.hotelProvider,
        cityName: hotel.cityName,
        stateName: hotel.stateName,
        countryName: hotel.countryName,
      };

      const hotelBook: HotelBook = {
        sessionToken: accountResult.sessionToken,
        value: hotel.total,
        currency: hotel.currencyCode,
        checkIn: hotel.checkinDate,
        checkOut: hotel.checkoutDate,
        searchId: hotel.searchId,
        optionId: hotel.optionId,
        distanceRestriction: search.distanceRestriction,
        rateId: roomRateId,
        rateValue: roomRatePrice.total,
        roomRate,
        travelers: [hotelTraveler],
        hotel: hotelDetails,
      };

      const data = travel
        .post<HotelValidateResponse>('Travels/Validate', {
          userId: traveler ? traveler.id || traveler.userId : accountResult?.id,
          userFullName: traveler
            ? `${traveler.name} ${traveler.lastName}`
            : `${accountResult?.name} ${accountResult?.lastName}`,
          companyId: accountResult?.companyId,
          currency: hotel.currencyCode,
          hotelBook,
          justifyForBreakingRules: justifyForBreakingRules,
          reason,
          travelArrangerUserId: traveler && traveler.id !== accountResult?.id ? accountResult?.id : undefined,
          costCenterId,
          billingCompanyId: branchCompany?.id,
          lowestLogicalFareList: search.lowestLogicalFareList,
        } as HotelSolicitationRequest)
        .then((response) => response.data);

      resolve(data);
    } catch (ex) {
      const checkInDate = hotel.checkinDate;
      const checkOutDate = hotel.checkoutDate;
      const event: TravelErrorEvent = {
        SOURCE: 'TRAVEL_HOME',
        RESULTS: 0,
        TRAVEL_PRODUCT_TYPE: 'HOTELS',
        TRAVEL_FROM: '',
        TRAVEL_TO: destinationPoint?.id.toString() ?? '',
        TRAVEL_DESTINATION_TYPE: search?.distanceRestriction ?? '',
        TRAVEL_TRIP_TYPE: '',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkInDate).toString(),
        TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
        TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
        SEARCHID: hotel.searchId ?? '',
        ERROR_MESSAGE: ex.toString(),
      };
      amplitudelogEvent(amplitudeEvents.TRAVEL_BOOKING_ERROR, event, accountResult);
      reject(ex);
    }
  });
